import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import {
  SupportRequest,
  SupportRequestModalContext,
  SupportRequestReason,
} from "@/app/entities/SupportRequest";
import { useWhitelabelContext } from "@/app/contexts/whitelabelContext";
import { supportRequestService } from "@/app/services/supportRequestService";
import { validateCpf } from "@/app/utils/cpfFormater";
import { masks } from "@/app/utils/masks";
import {
  SUPPORT_ALLOWED_IMAGE_MIMES,
  SUPPORT_ALLOWED_VIDEO_MIMES,
  SUPPORT_IMAGE_MAX_BYTES,
  SUPPORT_MAX_ATTACHMENTS,
  SUPPORT_VIDEO_MAX_BYTES,
} from "@/app/utils/supportRequest";
import { buildWhatsappUrl } from "@/app/utils/whatsapp";

export type SupportRequestStep = "choice" | "form";
type CreateMode = "form" | "whatsapp";

export interface SupportContact {
  name?: string;
  cpf?: string;
  phone?: string;
  email?: string;
}

interface UseSupportRequestModalParams {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unauthenticated?: boolean;
  context?: SupportRequestModalContext;
}

export function useSupportRequestModal({
  open,
  onOpenChange,
  unauthenticated = false,
  context,
}: UseSupportRequestModalParams) {
  const { appConfig } = useWhitelabelContext();
  const { pathname } = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<SupportRequestStep>(
    unauthenticated ? "choice" : "form",
  );
  const [reason, setReason] = useState<SupportRequestReason | null>(
    context?.reason ?? null,
  );
  const [description, setDescription] = useState(context?.description ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [contact, setContact] = useState<SupportContact>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [created, setCreated] = useState<SupportRequest | null>(null);

  const { data: reasons = [], isLoading: isLoadingReasons } = useQuery({
    queryKey: ["supportReasons"],
    queryFn: () => supportRequestService.getSupportReasons(),
    enabled: open,
    staleTime: 1000 * 60 * 10,
  });

  const reset = () => {
    setStep(unauthenticated ? "choice" : "form");
    setReason(context?.reason ?? null);
    setDescription(context?.description ?? "");
    setFiles([]);
    setContact({});
    setErrors({});
    setErrorMessage(null);
    setCreated(null);
  };

  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openWhatsapp = (ticketNumber?: string, isDirect = false) => {
    const phone = appConfig?.supportPhone || appConfig?.supportUrl;

    if (!phone) return;

    let message = "";

    if (ticketNumber && isDirect) {
      message = "Olá, gostaria de ajuda no login ou cadastro da plataforma.";
    } else if (ticketNumber) {
      const reasonLabel =
        reasons.find((item) => item.value === reason)?.label ?? "";
      message = `Olá, abri o chamado ${ticketNumber} — Motivo: ${reasonLabel}. Descrição: ${description.trim()}`;
    }

    window.open(buildWhatsappUrl(phone, message || undefined), "_blank");
  };

  const validateContact = () => {
    const newErrors: Record<string, string> = {};
    const phone = masks.number(contact.phone ?? "");

    if (!contact.name?.trim()) newErrors.name = "Nome é obrigatório";
    if (!contact.cpf || !validateCpf(contact.cpf))
      newErrors.cpf = "CPF inválido";
    if (contact.phone && phone.length < 10)
      newErrors.phone = "Telefone inválido";
    if (contact.email && !/^\S+@\S+\.\S+$/.test(contact.email))
      newErrors.email = "Email inválido";
    if (!contact.phone && !contact.email)
      newErrors.phone = "Informe telefone ou email";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (mode: CreateMode) => {
      const isWhatsappDirect = mode === "whatsapp";

      const request = unauthenticated
        ? await supportRequestService.createPublicSupportRequest({
            reason: isWhatsappDirect
              ? SupportRequestReason.WHATSAPP_DIRECT
              : reason!,
            screen: pathname,
            ...(!isWhatsappDirect && {
              name: contact.name?.trim(),
              cpf: contact.cpf ? masks.number(contact.cpf) : undefined,
              phone: contact.phone ? masks.number(contact.phone) : undefined,
              email: contact.email?.trim() || undefined,
              description: description.trim() || undefined,
            }),
          })
        : await supportRequestService.createSupportRequest({
            reason: isWhatsappDirect
              ? SupportRequestReason.WHATSAPP_DIRECT
              : reason!,
            screen: pathname,
            ...(!isWhatsappDirect && {
              description: description.trim() || undefined,
              product: context?.product,
              proposal: context?.proposal,
              transactionId: context?.transactionId,
              metadata: context?.metadata,
            }),
          });

      if (!isWhatsappDirect && files.length) {
        try {
          await supportRequestService.uploadSupportAttachments({
            id: request._id,
            files,
            user: unauthenticated
              ? {
                  name: contact.name?.trim(),
                  cpf: contact.cpf ? masks.number(contact.cpf) : undefined,
                }
              : undefined,
          });
        } catch (error) {
          console.error("[SupportRequest] erro ao enviar anexos", error);
        }
      }

      return { request, mode };
    },
    onSuccess: ({ request, mode }) => {
      if (mode === "whatsapp") {
        openWhatsapp(request.number, true);
        onOpenChange(false);
        return;
      }

      setCreated(request);
    },
    onError: (_error, mode) => {
      setErrorMessage(
        mode === "whatsapp"
          ? "Não foi possível abrir o WhatsApp. Tente novamente."
          : "Não foi possível abrir o chamado. Tente novamente.",
      );
    },
  });

  const handleSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";

    if (!selected.length) return;

    const accepted: File[] = [];

    for (const file of selected) {
      const isVideo = SUPPORT_ALLOWED_VIDEO_MIMES.includes(file.type);
      const isImage = SUPPORT_ALLOWED_IMAGE_MIMES.includes(file.type);

      if (!isVideo && !isImage) {
        setErrorMessage(
          `Arquivo não suportado: ${file.name}. Envie imagens (JPG, PNG, WEBP) ou vídeos (MP4, MOV).`,
        );
        continue;
      }

      const maxBytes = isVideo
        ? SUPPORT_VIDEO_MAX_BYTES
        : SUPPORT_IMAGE_MAX_BYTES;

      if (file.size > maxBytes) {
        setErrorMessage(
          `${file.name} excede o tamanho máximo de ${isVideo ? "50MB" : "10MB"}.`,
        );
        continue;
      }

      accepted.push(file);
    }

    if (accepted.length) {
      setFiles((prev) =>
        [...prev, ...accepted].slice(0, SUPPORT_MAX_ATTACHMENTS),
      );
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, position) => position !== index));
  };

  const handleOpenFilePicker = () => fileInputRef.current?.click();

  const updateContact = (field: keyof SupportContact, value: string) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;

      const rest = { ...prev };
      delete rest[field];

      return rest;
    });
  };

  const submitForm = () => {
    if (unauthenticated && !validateContact()) return;

    setErrorMessage(null);
    mutate("form");
  };

  const openDirectWhatsAppSupport = () => {
    if (isPending) return;

    setErrorMessage(null);
    mutate("whatsapp");
  };

  const canSubmit = unauthenticated
    ? !!contact.name?.trim() &&
      !!reason &&
      !isPending &&
      (!!contact.phone || !!contact.email)
    : !!reason && !isPending;

  const modalTitle = useMemo(() => {
    if (created) return "Solicitar ajuda";
    if (step === "choice") return "Precisa de ajuda?";

    return "Abrir chamado";
  }, [created, step]);

  return {
    step,
    setStep,
    reason,
    setReason,
    description,
    setDescription,
    files,
    fileInputRef,
    handleSelectFiles,
    handleRemoveFile,
    handleOpenFilePicker,
    contact,
    updateContact,
    errors,
    errorMessage,
    reasons,
    isLoadingReasons,
    isPending,
    canSubmit,
    created,
    modalTitle,
    unauthenticated,
    submitForm,
    openDirectWhatsAppSupport,
    openWhatsapp,
    maxAttachments: SUPPORT_MAX_ATTACHMENTS,
  };
}
